import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Circle,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import WinnerRank from "../components/WinnerRank";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getWcPool,
  getWcDraftState,
  randomizeDraft,
  makeWcPick,
  setWcAutodraft,
  getWcLeaderboard,
  getWcFixtures,
  getWcPayouts,
  getWcSmackMessages,
  postWcSmackMessage,
  getWcSmackReplies,
  postWcSmackReply,
  reactWcSmack,
  deleteWcSmackMessage,
} from "../api/worldCup";
import { FaTrophy } from "react-icons/fa";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import SmackBoard from "../components/SmackBoard";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";
const DIVIDER_GRAY = "#7a7a7a";
const GOLD         = "#c9a227";

function WcFinalResultsBox({ leaderboard, payouts, poolName }) {
  const sortedPayouts = [...payouts].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const hasPayouts = sortedPayouts.length > 0;
  const top3 = leaderboard.slice(0, 3);

  const header = (
    <Box bg={GOLD} px={6} py={3}>
      <HStack justify="center" spacing={3}>
        <Box color="white" fontSize="20px" lineHeight="1"><FaTrophy /></Box>
        <Heading fontFamily="'Orbitron', sans-serif"
          fontSize={{ base: "14px", md: "22px" }} letterSpacing="3px"
          fontWeight="700" color="white" textAlign="center">
          {poolName ? `${poolName.toUpperCase()} — FINAL RESULTS` : "FINAL RESULTS"}
        </Heading>
        <Box color="white" fontSize="20px" lineHeight="1"><FaTrophy /></Box>
      </HStack>
    </Box>
  );

  const rows = hasPayouts
    ? sortedPayouts.map((payout, i) => ({
        label: payout.label,
        amount: payout.amount,
        username: leaderboard[i]?.username ?? "—",
        pts: leaderboard[i]?.total_points ?? null,
        isFirst: i === 0,
      }))
    : top3.map((row, i) => ({
        label: i === 0 ? "1st Place" : i === 1 ? "2nd Place" : "3rd Place",
        amount: null,
        username: row.username,
        pts: row.total_points,
        isFirst: i === 0,
      }));

  if (rows.length === 0) return null;

  return (
    <Box bg="white" color="black" border={`3px solid ${GOLD}`} borderRadius="lg"
      boxShadow="lg" mb={6} overflow="hidden">
      {header}
      <Box px={6} py={4}>
        <Flex gap={4} align="stretch" flexWrap={{ base: "wrap", md: "nowrap" }}>
          {rows.map(({ label, amount, username, pts, isFirst }, i) => (
            <Box key={i}
              bg="yellow.50"
              border="2px solid"
              borderColor={isFirst ? GOLD : TOP_BAR_BLUE}
              borderRadius="lg"
              p={isFirst ? 6 : 4}
              flex="1"
              minW={isFirst ? { base: "100%", md: 0 } : { base: "calc(50% - 8px)", md: 0 }}
              display="flex" alignItems="center" justifyContent="center"
            >
              <VStack spacing={isFirst ? 3 : 2} align="center">
                <Box color={isFirst ? GOLD : TOP_BAR_BLUE}
                  fontSize={isFirst ? "48px" : "28px"} lineHeight="1">
                  <FaTrophy />
                </Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                  color="gray.500" letterSpacing="1px" textAlign="center">
                  {label}
                </Text>
                <Text fontSize={isFirst ? "2xl" : "lg"} fontWeight="bold"
                  color={TOP_BAR_BLUE} textAlign="center">
                  {username}
                </Text>
                {pts != null && (
                  <Text fontSize="sm" color="gray.600">{pts} pts</Text>
                )}
                {amount != null && (
                  <Text fontSize={isFirst ? "xl" : "lg"} fontWeight="bold" color={GOLD}>
                    ${Number(amount).toFixed(2)}
                  </Text>
                )}
              </VStack>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}
const WC_SEASON_ID = 2;

const WC_RULES = [
  {
    heading: "Group Stage",
    lines: [
      "Win: 3 pts per team",
      "Draw: 1 pt per team",
      "Goals Scored: +1 pt each",
      "Goals Allowed: -0.5 pts each",
    ],
  },
  {
    heading: "Knockout Stage",
    lines: [
      "Win (90 min / ET): 3 pts",
      "Win via Penalties: 2 pts",
    ],
  },
  {
    heading: "Bonus Points",
    lines: [
      "Round of 32: +2 pts",
      "Round of 16: +3 pts",
      "Quarterfinal: +5 pts",
      "Semifinal: +7 pts",
      "Final: +9 pts",
      "Champion: +6 pts",
    ],
  },
  {
    heading: "General",
    lines: [
      "Eliminated teams stop scoring",
      "Swapped-out teams are removed from roster along with their points",
      "Tiebreaker: Total goals",
    ],
  },
];

const STAGE_LABELS = {
  group:       "Group Stage",
  r32:         "Round of 32",
  r16:         "Round of 16",
  qf:          "Quarterfinals",
  sf:          "Semifinals",
  third_place: "Third Place",
  final:       "Final",
};


function FlagImg({ code, size = 24 }) {
  if (!code) return null;
  // flagcdn.com only serves specific heights; snap to nearest valid value
  const VALID_H = [15, 18, 24, 30, 36, 48, 60];
  const h = VALID_H.reduce((best, v) => Math.abs(v - size) < Math.abs(best - size) ? v : best);
  const w = Math.round(h * 4 / 3);
  return (
    <img
      src={`https://flagcdn.com/${w}x${h}/${code.toLowerCase()}.png`}
      alt={code}
      style={{ width: w, height: h, objectFit: "cover", borderRadius: 2, display: "inline-block", flexShrink: 0 }}
    />
  );
}


function toUtcDate(dt) {
  // Backend stores naive UTC datetimes (no Z). Append Z so JS treats them as UTC.
  if (!dt) return null;
  const s = String(dt);
  return new Date(s.endsWith("Z") || s.includes("+") ? s : s + "Z");
}

function formatTimeEST(dt) {
  const d = toUtcDate(dt);
  if (!d) return "TBD";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
  }) + " ET";
}

function formatDateEST(dt) {
  const d = toUtcDate(dt);
  if (!d) return "TBD";
  return d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "America/New_York",
  });
}

// ── Pending view ─────────────────────────────────────────────────────────────

function PendingView({ pool, isCreator, onBeginDraft, beginning }) {
  const draftTime = pool.draft_starts_at ? new Date(pool.draft_starts_at) : null;
  return (
    <VStack align="stretch" spacing={6}>
      <Box
        bg="white"
        color="black"
        border={`3px solid ${TOP_BAR_BLUE}`}
        borderRadius="lg"
        boxShadow="lg"
        p={8}
        textAlign="center"
      >
        <Heading size="md" color={TOP_BAR_BLUE} mb={3}>Waiting for Draft to Begin</Heading>
        {draftTime ? (
          <Text fontSize="lg">
            Draft starts: <strong>{draftTime.toLocaleString()}</strong>
          </Text>
        ) : (
          <Text color="gray.500">Draft start time has not been set yet.</Text>
        )}
        <Text mt={4} color="gray.600">
          {isCreator
            ? "When everyone is ready, click below to randomize the draft order and start the draft."
            : "The pool creator will randomize the draft order and kick off the draft when everyone is ready."}
        </Text>
        {isCreator && (
          <Button
            mt={6}
            bg={TOP_BAR_BLUE}
            color="white"
            _hover={{ bg: "#00004a" }}
            isLoading={beginning}
            loadingText="Starting..."
            onClick={onBeginDraft}
          >
            Begin Draft
          </Button>
        )}
      </Box>
      <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
        <Heading size="md" color={TOP_BAR_BLUE} mb={4}>Pool Summary</Heading>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {[
            { label: "Members",           value: pool.user_count },
            { label: "Max Users",         value: pool.max_users ?? "—" },
            { label: "Countries / User",  value: pool.teams_per_user },
            { label: "Buy-in",            value: pool.pool_depth ? `$${pool.pool_depth}` : "None" },
          ].map(({ label, value }) => (
            <Box key={label} textAlign="center" p={4} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color={TOP_BAR_BLUE}>{value}</Text>
              <Text fontSize="sm" color="gray.500">{label}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </VStack>
  );
}

// ── Draft view ────────────────────────────────────────────────────────────────

function DraftView({ draftState, currentUserId, isCreator, onPick, picking, viewMode, setViewMode, autodraftEnabled, onToggleAutodraft, togglingAutodraft }) {
  const {
    draft_order,
    teams,
    current_picker_user_id,
    draft_complete,
  } = draftState;

  const isMyTurn = current_picker_user_id === currentUserId;
  const currentPickerUsername = (draft_order || []).find(d => d.user_id === current_picker_user_id)?.username || "...";

  const [pendingTeam, setPendingTeam] = useState(null);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isAutodraftOpen, onOpen: onAutodraftOpen, onClose: onAutodraftClose } = useDisclosure();
  const { isOpen: isDraftOrderOpen, onOpen: onDraftOrderOpen, onClose: onDraftOrderClose } = useDisclosure();

  const canPick = !draft_complete && !picking && (isMyTurn || isCreator);

  function handleTeamClick(team) {
    setPendingTeam(team);
    onConfirmOpen();
  }

  function handleConfirm() {
    onConfirmClose();
    const behalf = (!isMyTurn && isCreator) ? current_picker_user_id : null;
    onPick(pendingTeam.team_id, behalf);
    setPendingTeam(null);
  }

  function handleCancel() {
    onConfirmClose();
    setPendingTeam(null);
  }

  // Group teams by group letter
  const groups = {};
  for (const t of teams || []) {
    const g = t.group || "?";
    if (!groups[g]) groups[g] = [];
    groups[g].push(t);
  }
  const groupKeys = Object.keys(groups).sort();

  // Color per draft position
  const posColors = [
    "blue.400", "green.400", "orange.400", "red.400", "purple.400",
    "teal.400", "pink.400", "yellow.500", "cyan.400", "gray.500",
    "blue.600", "green.600",
  ];

  function userColor(userId) {
    const pos = (draft_order || []).findIndex((d) => d.user_id === userId);
    return posColors[pos] ?? "gray.400";
  }

  // Sorted teams for rank view
  const teamsSortedByRank = [...(teams || [])].sort((a, b) => (a.fifa_rank ?? 999) - (b.fifa_rank ?? 999));

  // Split groups into two columns: A–F (first 6) and G+ (rest)
  const col1Keys = groupKeys.slice(0, 6);
  const col2Keys = groupKeys.slice(6);

  function renderTeam(t) {
    const isPicked   = t.is_picked;
    const isPickable = !isPicked && canPick;
    const pickerColor = isPicked ? userColor(t.picked_by_user_id) : "transparent";
    return (
      <Box
        key={t.team_id}
        bg="white"
        color="black"
        border="2px solid"
        borderColor={isPicked ? pickerColor : "gray.200"}
        borderRadius="md"
        p={2}
        opacity={isPicked ? 0.65 : 1}
        cursor={isPickable ? "pointer" : "default"}
        _hover={isPickable ? { borderColor: TOP_BAR_BLUE, transform: "translateY(-2px)", boxShadow: "md" } : {}}
        transition="0.15s"
        onClick={() => isPickable && handleTeamClick(t)}
        position="relative"
      >
        <HStack spacing={2} align="center">
          {/* ── Flag size controlled here: change the size prop below ── */}
          <FlagImg code={t.flag_code} size={24} />
          <VStack spacing={0} align="start" flex="1" minW={0}>
            <Text fontSize="xs" fontWeight="bold" noOfLines={1}>
              {t.name} <Text as="span" fontWeight="normal" color="gray.500">(#{t.fifa_rank})</Text>
            </Text>
            <Text fontSize="2xs" color="gray.500" noOfLines={1} visibility={isPicked ? "visible" : "hidden"}>
              {t.picked_by_username || "\u00A0"}
            </Text>
          </VStack>
        </HStack>
        {picking === t.team_id && (
          <Flex position="absolute" inset={0} align="center" justify="center" bg="whiteAlpha.800" borderRadius="md">
            <Spinner size="sm" />
          </Flex>
        )}
      </Box>
    );
  }

  function renderGroupColumn(keys) {
    return (
      <VStack align="stretch" spacing={3} flex="1">
        {keys.map((g) => (
          <Box key={g}>
            <Box
              bg={TOP_BAR_BLUE}
              color="white"
              border={`1px solid ${DIVIDER_GRAY}`}
              borderRadius="md"
              px={3}
              py={1}
              fontSize="sm"
              fontWeight="bold"
              letterSpacing="1px"
              mb={2}
              textAlign="center"
            >
              GROUP {g}
            </Box>
            <SimpleGrid columns={2} spacing={2}>
              {groups[g].map(renderTeam)}
            </SimpleGrid>
          </Box>
        ))}
      </VStack>
    );
  }

  function renderRankView() {
    // Build group → other team names lookup
    const groupOthers = {};
    for (const t of teams || []) {
      const g = t.group || "?";
      if (!groupOthers[g]) groupOthers[g] = [];
      groupOthers[g].push(t.name);
    }

    return (
      <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" overflowX="auto">
        <Table variant="simple" style={{ tableLayout: "fixed", width: "100%" }}>
          <Thead>
            <Tr>
              <Th w="100px" whiteSpace="nowrap">FIFA Rank</Th>
              <Th w="60px" px={2}></Th>
              <Th w="160px">Country</Th>
              <Th w="100px" whiteSpace="nowrap">Group</Th>
              <Th w="300px" whiteSpace="nowrap">Group Countries</Th>
              <Th w="100px">Picked By</Th>
              <Th w="80px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {teamsSortedByRank.map((t) => {
              const isPicked    = t.is_picked;
              const isPickable  = !isPicked && canPick;
              const pickerColor = isPicked ? userColor(t.picked_by_user_id) : undefined;
              const others      = (groupOthers[t.group] || []).filter((n) => n !== t.name).join(", ");
              return (
                <Tr
                  key={t.team_id}
                  bg={isPicked ? "gray.50" : "white"}
                  opacity={isPicked ? 0.7 : 1}
                  _hover={isPickable ? { bg: "blue.50", cursor: "pointer" } : {}}
                  onClick={() => isPickable && handleTeamClick(t)}
                  h="44px"
                >
                  <Td fontWeight="bold" color="gray.500" whiteSpace="nowrap" py={2}>#{t.fifa_rank}</Td>
                  <Td px={2} py={2}><FlagImg code={t.flag_code} size={24} /></Td>
                  <Td fontWeight="medium" py={2} overflow="hidden">
                    <Text noOfLines={1}>{t.name}</Text>
                  </Td>
                  <Td whiteSpace="nowrap" color="gray.500" py={2}>Group {t.group}</Td>
                  <Td py={2} overflow="hidden">
                    <Text fontSize="sm" color="gray.500" noOfLines={1}>{others}</Text>
                  </Td>
                  <Td py={2} overflow="hidden">
                    {isPicked ? (
                      <HStack spacing={1}>
                        <Box w="8px" h="8px" borderRadius="full" bg={pickerColor} flexShrink={0} />
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>{t.picked_by_username}</Text>
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.300">—</Text>
                    )}
                  </Td>
                  <Td py={2} textAlign="right">
                    {!isPicked && isPickable && (
                      <Button
                        size="xs"
                        bg={TOP_BAR_BLUE}
                        color="white"
                        _hover={{ bg: "#00004a" }}
                        onClick={(e) => { e.stopPropagation(); handleTeamClick(t); }}
                      >
                        Draft
                      </Button>
                    )}
                    {picking === t.team_id && <Spinner size="xs" />}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  }

  return (
    <>
      {draft_complete && (
        <Box mb={4} p={3} borderRadius="md" bg="green.100" border="2px solid" borderColor="green.400">
          <Text fontWeight="bold" color="green.800">Draft complete! The pool is now active.</Text>
        </Box>
      )}

      {/* Creator context banner when picking on behalf of someone */}
      {!draft_complete && isCreator && !isMyTurn && (
        <Box mb={3} p={2} borderRadius="md" bg="orange.50" border="1px solid" borderColor="orange.300">
          <Text fontSize="sm" color="orange.800" fontWeight="medium">
            Creator mode — you can pick for <strong>{currentPickerUsername}</strong>
          </Text>
        </Box>
      )}

      <HStack mb={2} justify="space-between" flexWrap="wrap" gap={1}>
        <HStack spacing={1}>
          <Button
            size="xs"
            variant="outline"
            color="white"
            borderColor="whiteAlpha.500"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={() => setViewMode((m) => m === "group" ? "rank" : "group")}
          >
            {viewMode === "group" ? "Rank View" : "Group View"}
          </Button>
          {/* Mobile-only Draft Order button */}
          <Button
            display={{ base: "flex", md: "none" }}
            size="xs"
            variant="outline"
            color="white"
            borderColor="whiteAlpha.500"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={onDraftOrderOpen}
          >
            Draft Order
          </Button>
        </HStack>
        {!draft_complete && (
          <Button
            size="xs"
            bg={autodraftEnabled ? "orange.400" : "whiteAlpha.200"}
            color="white"
            border="1px solid"
            borderColor={autodraftEnabled ? "orange.400" : "whiteAlpha.400"}
            _hover={{ bg: autodraftEnabled ? "orange.500" : "whiteAlpha.300" }}
            onClick={onAutodraftOpen}
            isLoading={togglingAutodraft}
            loadingText="Saving..."
          >
            {autodraftEnabled ? "Autodraft ON" : "Autodraft OFF"}
          </Button>
        )}
      </HStack>

      {viewMode === "group" ? (
        <HStack align="start" spacing={4}>
          {renderGroupColumn(col1Keys)}
          {col2Keys.length > 0 && renderGroupColumn(col2Keys)}
        </HStack>
      ) : (
        renderRankView()
      )}

    {/* Confirm pick modal */}
    <Modal isOpen={isConfirmOpen} onClose={handleCancel} isCentered size="sm" motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
        <ModalHeader color={TOP_BAR_BLUE}>Confirm Pick</ModalHeader>
        <ModalCloseButton color={TOP_BAR_BLUE} />
        <ModalBody>
          {pendingTeam && (
            <VStack spacing={3} align="center" py={2}>
              <FlagImg code={pendingTeam.flag_code} size={48} />
              <Text fontSize="lg" fontWeight="bold">{pendingTeam.name}</Text>
              <Text fontSize="sm" color="gray.500">FIFA Rank #{pendingTeam.fifa_rank}</Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                {isCreator && !isMyTurn
                  ? <>Drafting <strong>{pendingTeam.name}</strong> on behalf of <strong>{currentPickerUsername}</strong></>
                  : <>Are you sure you want to draft <strong>{pendingTeam.name}</strong>?</>}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
            <Button bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} onClick={handleConfirm}>
              Confirm Pick
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Autodraft confirmation modal */}
    <Modal isOpen={isAutodraftOpen} onClose={onAutodraftClose} isCentered size="sm" motionPreset="scale">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
        <ModalHeader color={TOP_BAR_BLUE}>
          {autodraftEnabled ? "Disable Autodraft?" : "Enable Autodraft?"}
        </ModalHeader>
        <ModalCloseButton color={TOP_BAR_BLUE} />
        <ModalBody>
          <Text fontSize="sm" color="gray.700">
            {autodraftEnabled
              ? "Autodraft is currently ON. Turning it off means you'll need to make your picks manually."
              : "Autodraft will automatically pick the highest-ranked available team whenever it's your turn. You can turn it off at any time."}
          </Text>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={onAutodraftClose}>Cancel</Button>
            <Button
              bg={autodraftEnabled ? "red.500" : "orange.400"}
              color="white"
              _hover={{ bg: autodraftEnabled ? "red.600" : "orange.500" }}
              onClick={() => { onAutodraftClose(); onToggleAutodraft(); }}
            >
              {autodraftEnabled ? "Turn Off" : "Turn On"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Mobile Draft Order drawer */}
    <Drawer isOpen={isDraftOrderOpen} onClose={onDraftOrderClose} placement="bottom">
      <DrawerOverlay />
      <DrawerContent maxH="80vh" borderTopRadius="lg">
        <DrawerCloseButton />
        <DrawerHeader color={TOP_BAR_BLUE} borderBottom="1px solid" borderColor="gray.200">
          Draft Order
          {!draft_complete && (
            <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={0.5}>
              Round {draftState.current_round} · Pick {draftState.total_picks + 1} / {draftState.max_picks}
            </Text>
          )}
        </DrawerHeader>
        <DrawerBody overflowY="auto" py={3}>
          <VStack align="stretch" spacing={2}>
            {(draft_order || []).map((d, idx) => {
              const posColors = ["blue.400","green.400","orange.400","red.400","purple.400","teal.400","pink.400","yellow.500","cyan.400","gray.500","blue.600","green.600"];
              const color = posColors[idx] ?? "gray.400";
              return (
                <Box key={d.user_id} p={2} borderRadius="md"
                  bg={d.is_current_picker ? "yellow.50" : "gray.50"}
                  border={d.is_current_picker ? "2px solid" : "1px solid"}
                  borderColor={d.is_current_picker ? "yellow.400" : "gray.200"}
                >
                  <HStack spacing={2} mb={1}>
                    <Circle size="22px" bg={color} color="white" fontSize="xs" fontWeight="bold">{d.position}</Circle>
                    <Text fontSize="sm" fontWeight={d.is_current_picker ? "bold" : "normal"} color={TOP_BAR_BLUE} flex={1} noOfLines={1}>
                      {d.username}{d.is_current_picker && !draft_complete && " ⬅"}
                    </Text>
                    {d.autodraft_enabled && (
                      <Box fontSize="10px" fontWeight="bold" color="white" bg="orange.400" borderRadius="sm" px="4px" py="1px" flexShrink={0}>A</Box>
                    )}
                  </HStack>
                  {d.picks && d.picks.length > 0 ? (
                    <VStack align="start" spacing={0} pl={7}>
                      {d.picks.map((p) => (
                        <Text key={p.team_id} fontSize="xs" color="gray.600" noOfLines={1}>{p.team_name}</Text>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="xs" color="gray.500" pl={7}>No picks yet</Text>
                  )}
                </Box>
              );
            })}
          </VStack>
          <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={3}>A = Autodraft</Text>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
    </>
  );
}

// ── Leaderboard view ──────────────────────────────────────────────────────────

function LeaderboardView({ leaderboard, currentUsername, poolName, poolFinished }) {
  const [selected, setSelected] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [lbSortField, setLbSortField] = useState("total_points");
  const [lbSortDir,   setLbSortDir]   = useState("desc");

  const sortedLeaderboard = useMemo(() => {
    const arr = [...leaderboard];
    arr.sort((a, b) => {
      const av = a[lbSortField] ?? -1;
      const bv = b[lbSortField] ?? -1;
      return lbSortDir === "desc" ? bv - av : av - bv;
    });
    // `rank` here is the row's position under whatever sort is applied, so the
    // server's standing rank is kept alongside it — the trophy belongs to the
    // pool winner however the reader has chosen to sort.
    return arr.map((e, i) => ({ ...e, rank: i + 1, standing_rank: e.rank }));
  }, [leaderboard, lbSortField, lbSortDir]);

  function handleLbSort(field) {
    if (field === lbSortField) {
      setLbSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setLbSortField(field);
      setLbSortDir("desc");
    }
  }

  function SortTh({ field, children, ...props }) {
    const active = lbSortField === field;
    return (
      <Th
        cursor="pointer"
        userSelect="none"
        onClick={() => handleLbSort(field)}
        _hover={{ bg: active ? undefined : "blue.100" }}
        {...props}
      >
        {children}{active ? (lbSortDir === "desc" ? " ▼" : " ▲") : ""}
      </Th>
    );
  }

  function openEntry(entry) {
    setSelected(entry);
    onOpen();
  }

  return (
    <>
      <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
        <Heading size="md" color={TOP_BAR_BLUE} mb={4}>{poolName ? `${poolName} Leaderboard` : "Leaderboard"}</Heading>
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th w="36px" px={1} whiteSpace="nowrap">#</Th>
                <Th minW="140px">Player</Th>
                <SortTh field="total_points" minW="70px" textAlign="center" bg="blue.200" fontWeight="bold" whiteSpace="nowrap">Total</SortTh>
                <SortTh field="group_stage_points" minW="85px" textAlign="center" bg="blue.50" whiteSpace="nowrap">Group Pts</SortTh>
                <SortTh field="knockout_points" minW="85px" textAlign="center" bg="blue.50" whiteSpace="nowrap">KO Pts</SortTh>
                <SortTh field="bonus_points" minW="85px" textAlign="center" bg="blue.50" whiteSpace="nowrap">Bonus Pts</SortTh>
                <Th pl={4} bg="green.50" whiteSpace="nowrap">Countries</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedLeaderboard.length > 0 ? (
                sortedLeaderboard.map((entry) => {
                  const isMe = (entry.username || "").toLowerCase() === (currentUsername || "").toLowerCase();
                  const allTeams = (entry.teams || []).filter((t) => !t.is_swapped_out);
                  return (
                    <Tr key={entry.entry_id} bg={isMe ? "yellow.50" : "transparent"}>
                      <Td px={1} whiteSpace="nowrap" fontWeight={isMe ? "bold" : "normal"}>
                        <WinnerRank rank={entry.rank} finished={poolFinished}
                                    isWinner={entry.standing_rank === 1} />
                      </Td>
                      <Td fontWeight={isMe ? "bold" : "normal"}>
                        <HStack spacing={2}>
                          <Text
                            color={TOP_BAR_BLUE}
                            cursor="pointer"
                            textDecoration="underline"
                            _hover={{ color: "#00004a" }}
                            onClick={() => openEntry(entry)}
                          >
                            {entry.username}
                          </Text>
                          {entry.swap_used && <Badge colorScheme="purple" fontSize="2xs">SWAP</Badge>}
                        </HStack>
                      </Td>
                      <Td textAlign="center" fontWeight="bold" color={TOP_BAR_BLUE} style={{ background: isMe ? "#90cdf4" : "#bee3f8" }}>{entry.total_points}</Td>
                      <Td textAlign="center" style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.group_stage_points ?? "—"}</Td>
                      <Td textAlign="center" style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.knockout_points ?? "—"}</Td>
                      <Td textAlign="center" style={{ background: isMe ? "#ebf8ff" : "#f0f8ff" }}>{entry.bonus_points ?? "—"}</Td>
                      <Td fontSize="sm" pl={4} whiteSpace="nowrap" style={{ background: isMe ? "#f0fff4" : "#f6fff9" }}>
                        {allTeams.length > 0 ? allTeams.map((t, idx) => (
                          <Text
                            as="span"
                            key={t.team_id}
                            color={t.is_active ? "gray.700" : "gray.400"}
                            textDecoration={t.is_active ? "none" : "line-through"}
                          >
                            {t.team_name}{idx < allTeams.length - 1 ? ", " : ""}
                          </Text>
                        )) : <Text as="span" color="gray.400">—</Text>}
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr><Td colSpan={7}><Text color="gray.500">No entries yet.</Text></Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
        <Text fontSize="xs" color="gray.400" mt={3}>*Click on a player name to see point breakdown by country</Text>
      </Box>

      {selected && (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg">
            <ModalHeader color={TOP_BAR_BLUE}>{selected.username}'s Countries</ModalHeader>
            <ModalCloseButton color={TOP_BAR_BLUE} />
            <ModalBody pb={6}>
              <Box overflowX="auto">
              <Table size="sm" variant="simple" style={{ tableLayout: "fixed", width: "100%" }}>
                <Thead>
                  <Tr>
                    <Th w="125px">Country</Th>
                    <Th w="75px" textAlign="center">Group</Th>
                    <Th w="75px" textAlign="center">Group Pts</Th>
                    <Th w="75px" textAlign="center">KO Pts</Th>
                    <Th w="75px" textAlign="center">Bonus Pts</Th>
                    <Th w="75px" textAlign="center">Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(selected.teams || []).map((t) => (
                    <Tr key={t.team_id} opacity={t.is_active ? 1 : 0.45}>
                      <Td fontWeight="medium" pr={4}>
                        <HStack spacing={2}>
                          {t.flag_code && <FlagImg code={t.flag_code} size={24} />}
                          <Text as="span" noOfLines={1}>{t.team_name}</Text>
                          {!t.is_active && <Badge colorScheme="gray" fontSize="2xs">OUT</Badge>}
                          {t.is_swapped_out && <Badge colorScheme="purple" fontSize="2xs">SWAPPED</Badge>}
                        </HStack>
                      </Td>
                      <Td textAlign="center">{t.group}</Td>
                      <Td textAlign="center">{t.group_stage?.points ?? "—"}</Td>
                      <Td textAlign="center">{t.knockout?.win_points ?? "—"}</Td>
                      <Td textAlign="center">{t.knockout?.bonus_points ?? "—"}</Td>
                      <Td textAlign="center" fontWeight="bold" color={TOP_BAR_BLUE}>{t.points}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              </Box>
              <HStack justify="flex-end" mt={3} pt={3} borderTop="1px solid" borderColor="gray.200">
                <Text fontWeight="bold">Total: {selected.total_points} pts</Text>
              </HStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

// ── Full Teams view ────────────────────────────────────────────────────────────

function FullTeamsView({ leaderboard, poolFinished }) {
  const COL_W = 40; // px — all stat columns the same width
  const thStyle = {
    textAlign: "center",
    fontSize: "11px",
    padding: "6px 4px",
    whiteSpace: "nowrap",
    borderBottom: "2px solid #e2e8f0",
    width: COL_W,
  };
  const groupHdr = { ...thStyle, background: "#ebf8ff", color: "#2b6cb0" };
  const koHdr    = { ...thStyle, background: "#faf5ff", color: "#6b21a8" };
  const bonusHdr = { ...thStyle, background: "#f0fff4", color: "#276749" };
  const tdStyle  = { textAlign: "center", padding: "5px 4px", fontSize: "13px", width: COL_W };

  return (
    <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" overflowX="auto">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
            <th rowSpan={2} style={{ ...thStyle, width: 36 }}>#</th>
            <th rowSpan={2} style={{ ...thStyle, textAlign: "left", paddingLeft: 8 }}>Player</th>
            <th rowSpan={2} style={{ ...thStyle, textAlign: "left", paddingLeft: 8 }}>Team</th>
            <th colSpan={4} style={{ ...groupHdr, borderBottom: "1px solid #bee3f8" }}>Group Stage</th>
            <th colSpan={2} style={{ ...koHdr, borderBottom: "1px solid #e9d8fd" }}>Knockout Stage</th>
            <th colSpan={6} style={{ ...bonusHdr, borderBottom: "1px solid #c6f6d5" }}>Bonus</th>
            <th rowSpan={2} style={{ ...thStyle }}>Total</th>
          </tr>
          <tr>
            <th style={groupHdr}>W</th>
            <th style={groupHdr}>D</th>
            <th style={groupHdr}>GS</th>
            <th style={groupHdr}>GA</th>
            <th style={koHdr}>Win (90/ET)</th>
            <th style={koHdr}>Win (PK)</th>
            <th style={bonusHdr}>R32</th>
            <th style={bonusHdr}>R16</th>
            <th style={bonusHdr}>QF</th>
            <th style={bonusHdr}>SF</th>
            <th style={bonusHdr}>Final</th>
            <th style={bonusHdr}>Champ</th>
          </tr>
        </thead>
        <tbody>
          {leaderboard.map((entry) => {
            const teams = (entry.teams || []);
            const lastTi = teams.length - 1;
            return teams.map((t, ti) => {
              const gs = t.group_stage || {};
              const ko = t.knockout || {};
              const bonus = ko.bonus_by_stage || {};
              const isFirstRow = ti === 0;
              const isLastRow  = ti === lastTi;
              // Heavy border above first row of each user, thin border between countries
              const userBorder    = "3px solid #a0aec0";
              const countryBorder = "1px solid #e2e8f0";
              const topBorder = isFirstRow ? userBorder : countryBorder;
              const rowBg = entry.rank % 2 === 0 ? "#f7fafc" : "white";
              return (
                <tr key={`${entry.entry_id}-${t.team_id}`} style={{ background: rowBg }}>
                  {isFirstRow && (
                    <>
                      <td rowSpan={teams.length} style={{ ...tdStyle, borderTop: userBorder, borderBottom: userBorder, fontWeight: "bold" }}>
                        <WinnerRank rank={entry.rank} finished={poolFinished} />
                      </td>
                      <td rowSpan={teams.length} style={{ ...tdStyle, borderTop: userBorder, borderBottom: userBorder, textAlign: "left", paddingLeft: 8, fontWeight: "bold" }}>
                        {entry.username}
                        {entry.swap_used && (
                          <span style={{ marginLeft: 4, fontSize: 10, background: "#805ad5", color: "white", borderRadius: 4, padding: "1px 4px" }}>SWAP</span>
                        )}
                      </td>
                    </>
                  )}
                  <td style={{ ...tdStyle, borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder, textAlign: "left", paddingLeft: 8, width: "auto" }}>
                    <span style={{ opacity: t.is_active ? 1 : 0.45, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {t.flag_code && (
                        <img
                          src={`https://flagcdn.com/32x24/${t.flag_code.toLowerCase()}.png`}
                          alt={t.flag_code}
                          style={{ width: 21, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
                        />
                      )}
                      {t.team_name}
                      {!t.is_active && <span style={{ marginLeft: 4, fontSize: 10, background: "#718096", color: "white", borderRadius: 4, padding: "1px 4px" }}>OUT</span>}
                      {t.is_swapped_out && <span style={{ marginLeft: 4, fontSize: 10, background: "#805ad5", color: "white", borderRadius: 4, padding: "1px 4px" }}>SW</span>}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, background: "#ebf8ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{gs.wins ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#ebf8ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{gs.draws ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#ebf8ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{gs.goals_scored ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#ebf8ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{gs.goals_allowed ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#faf5ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{ko.wins_regular ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#faf5ff", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{ko.wins_pk ?? 0}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.r32 ? `+${bonus.r32}` : "—"}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.r16 ? `+${bonus.r16}` : "—"}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.qf ? `+${bonus.qf}` : "—"}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.sf ? `+${bonus.sf}` : "—"}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.final ? `+${bonus.final}` : "—"}</td>
                  <td style={{ ...tdStyle, background: "#f0fff4", borderTop: topBorder, borderBottom: isLastRow ? userBorder : countryBorder }}>{bonus.champion ? `+${bonus.champion}` : "—"}</td>
                  {isFirstRow && (
                    <td rowSpan={teams.length} style={{ ...tdStyle, borderTop: userBorder, borderBottom: userBorder, fontWeight: "bold", color: TOP_BAR_BLUE, fontSize: 14 }}>
                      {entry.total_points}
                    </td>
                  )}
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </Box>
  );
}

// ── Fixtures view ─────────────────────────────────────────────────────────────

function FixtureCard({ fx, teamOwners, showGroup = false, showDate = false }) {
  const played = fx.is_final;
  const homeWon = played && (
    fx.penalty_winner_id ? fx.penalty_winner_id === fx.home_team_id
      : (fx.home_goals ?? 0) > (fx.away_goals ?? 0)
  );
  const awayWon = played && (
    fx.penalty_winner_id ? fx.penalty_winner_id === fx.away_team_id
      : (fx.away_goals ?? 0) > (fx.home_goals ?? 0)
  );
  const homeOwner = teamOwners?.[fx.home_team];
  const awayOwner = teamOwners?.[fx.away_team];

  const stageLabel = showGroup && fx.group
    ? `Group ${fx.group}`
    : showGroup && fx.stage !== "group"
      ? (STAGE_LABELS[fx.stage] ?? fx.stage)
      : null;

  return (
    <Box bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" px={3} py={2}>
      {/* ── Desktop layout ── */}
      <HStack display={{ base: "none", md: "flex" }} spacing={3} align="center">
        <VStack align="start" spacing={0} w={showDate ? "200px" : "140px"} flexShrink={0}>
          {showDate && fx.kickoff_utc && (
            <Text fontSize="xs" color="gray.500" fontWeight="medium">{formatDateEST(fx.kickoff_utc)}</Text>
          )}
          <Text fontSize="sm" color="gray.700" fontWeight="semibold">
            {fx.kickoff_utc ? formatTimeEST(fx.kickoff_utc) : "TBD"}
          </Text>
          {stageLabel && <Text fontSize="xs" color="gray.500">{stageLabel}</Text>}
        </VStack>
        <HStack flex="1" justify="flex-end" spacing={1} minW={0}>
          <VStack align="end" spacing={0} minW={0}>
            <Text fontSize="sm" fontWeight={homeWon ? "bold" : "normal"} color={homeWon ? TOP_BAR_BLUE : "black"} noOfLines={1}>{fx.home_team ?? "TBD"}</Text>
            {homeOwner && <Text fontSize="2xs" color="gray.400" noOfLines={1}>{homeOwner}</Text>}
          </VStack>
          <FlagImg code={fx.home_flag_code} size={24} />
        </HStack>
        <Box px={2} py={1} w="64px" flexShrink={0} textAlign="center"
          bg={played ? TOP_BAR_BLUE : "gray.200"} color={played ? "white" : "gray.600"}
          borderRadius="sm" fontWeight="bold" fontSize="sm">
          {played ? `${fx.home_goals ?? 0} – ${fx.away_goals ?? 0}${fx.penalty_winner_id ? " P" : ""}` : "vs"}
        </Box>
        <HStack flex="1" justify="flex-start" spacing={1} minW={0}>
          <FlagImg code={fx.away_flag_code} size={24} />
          <VStack align="start" spacing={0} minW={0}>
            <Text fontSize="sm" fontWeight={awayWon ? "bold" : "normal"} color={awayWon ? TOP_BAR_BLUE : "black"} noOfLines={1}>{fx.away_team ?? "TBD"}</Text>
            {awayOwner && <Text fontSize="2xs" color="gray.400" noOfLines={1}>{awayOwner}</Text>}
          </VStack>
        </HStack>
        <Text fontSize="xs" color="gray.400" noOfLines={2} textAlign="right" w={showDate ? "200px" : "140px"} flexShrink={0}>
          {fx.venue ?? ""}
        </Text>
      </HStack>

      {/* ── Mobile layout ── */}
      <Box display={{ base: "block", md: "none" }}>
        {/* Top row: time · group · date · venue */}
        <HStack spacing={1} mb={2} flexWrap="wrap">
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">
            {fx.kickoff_utc ? formatTimeEST(fx.kickoff_utc) : "TBD"}
          </Text>
          {stageLabel && <Text fontSize="xs" color="gray.400">· {stageLabel}</Text>}
          {showDate && fx.kickoff_utc && (
            <Text fontSize="xs" color="gray.400">· {formatDateEST(fx.kickoff_utc)}</Text>
          )}
          {fx.venue && <Text fontSize="xs" color="gray.400" noOfLines={1}>· {fx.venue}</Text>}
        </HStack>
        {/* Match row: name flag | score | flag name */}
        <Flex align="center" gap={1}>
          <Box flex={1} minW={0} textAlign="right">
            <Text fontSize="sm" fontWeight={homeWon ? "bold" : "normal"} color={homeWon ? TOP_BAR_BLUE : "black"} noOfLines={1}>{fx.home_team ?? "TBD"}</Text>
            {homeOwner && <Text fontSize="2xs" color="gray.400" noOfLines={1}>{homeOwner}</Text>}
          </Box>
          <FlagImg code={fx.home_flag_code} size={30} />
          <Box px={2} py={1} flexShrink={0} textAlign="center"
            bg={played ? TOP_BAR_BLUE : "gray.200"} color={played ? "white" : "gray.600"}
            borderRadius="sm" fontWeight="bold" fontSize="xs" whiteSpace="nowrap">
            {played ? `${fx.home_goals ?? 0}–${fx.away_goals ?? 0}${fx.penalty_winner_id ? "P" : ""}` : "vs"}
          </Box>
          <FlagImg code={fx.away_flag_code} size={30} />
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight={awayWon ? "bold" : "normal"} color={awayWon ? TOP_BAR_BLUE : "black"} noOfLines={1}>{fx.away_team ?? "TBD"}</Text>
            {awayOwner && <Text fontSize="2xs" color="gray.400" noOfLines={1}>{awayOwner}</Text>}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}


function BracketView({ fixtures, teamOwners }) {
  const SLOT_H = 100;   // vertical space per match slot
  const CARD_H = 80;    // fixed match card height
  const COL_W  = 180;   // column width
  const CONN_W = 30;    // connector width
  const HALF_H = 8 * SLOT_H;

  function cardTop(slotIdx, numSlots) {
    return (slotIdx + 0.5) * (HALF_H / numSlots) - CARD_H / 2;
  }

  const byStage = {};
  for (const fx of fixtures) {
    if (!byStage[fx.stage]) byStage[fx.stage] = [];
    byStage[fx.stage].push(fx);
  }

  // Sort R32 by bracket_slot (manually set), fall back to match_number
  const r32Raw = byStage["r32"] || [];
  const r32AllHaveSlot = r32Raw.length > 0 && r32Raw.every((fx) => fx.bracket_slot != null);
  const r32 = r32AllHaveSlot
    ? [...r32Raw].sort((a, b) => a.bracket_slot - b.bracket_slot)
    : [...r32Raw].sort((a, b) => (a.match_number ?? 0) - (b.match_number ?? 0));

  if (!r32.length && !(byStage["r16"] || []).length && !(byStage["qf"] || []).length &&
      !(byStage["sf"] || []).length && !(byStage["final"] || []).length) {
    return <Text color="gray.500" fontSize="sm">Knockout bracket not yet available.</Text>;
  }

  // Build team_id → R32 bracket_slot so later rounds can be placed correctly.
  const teamR32Slot = {};
  for (const fx of r32) {
    if (fx.bracket_slot) {
      if (fx.home_team_id) teamR32Slot[fx.home_team_id] = fx.bracket_slot;
      if (fx.away_team_id) teamR32Slot[fx.away_team_id] = fx.bracket_slot;
    }
  }

  // Place each fixture at its correct array index so sparse rounds render in
  // the right bracket slot. Formula: idx = floor((minR32Slot - 1) / slotSize)
  // where slotSize = 16 / totalSlots (R16→2, QF→4, SF→8).
  function placeInBracket(fxList, totalSlots) {
    const slotSize = 16 / totalSlots;
    const result = Array(totalSlots).fill(null);
    for (const fx of fxList) {
      const minSlot = Math.min(
        teamR32Slot[fx.home_team_id] ?? 999,
        teamR32Slot[fx.away_team_id] ?? 999
      );
      if (minSlot < 999) {
        const idx = Math.floor((minSlot - 1) / slotSize);
        if (idx >= 0 && idx < totalSlots) result[idx] = fx;
      }
    }
    return result;
  }

  function getWinner(fx) {
    if (!fx || !fx.is_final) return null;
    if (fx.penalty_winner_id) {
      return fx.penalty_winner_id === fx.home_team_id
        ? { team_id: fx.home_team_id, name: fx.home_team, flag: fx.home_flag_code }
        : { team_id: fx.away_team_id, name: fx.away_team, flag: fx.away_flag_code };
    }
    if ((fx.home_goals ?? 0) > (fx.away_goals ?? 0))
      return { team_id: fx.home_team_id, name: fx.home_team, flag: fx.home_flag_code };
    if ((fx.away_goals ?? 0) > (fx.home_goals ?? 0))
      return { team_id: fx.away_team_id, name: fx.away_team, flag: fx.away_flag_code };
    return null;
  }

  function makeSynthetic(stage, w1, w2) {
    return {
      _synthetic: true, stage,
      home_team_id: w1?.team_id ?? null, home_team: w1?.name ?? "TBD", home_flag_code: w1?.flag ?? null,
      away_team_id: w2?.team_id ?? null, away_team: w2?.name ?? "TBD", away_flag_code: w2?.flag ?? null,
      is_final: false, home_goals: null, away_goals: null, penalty_winner_id: null,
    };
  }

  // Build R32 winner map: bracket_slot → winner info
  const r32WinnerBySlot = {};
  for (const fx of r32) {
    if (fx.bracket_slot) {
      const w = getWinner(fx);
      if (w) r32WinnerBySlot[fx.bracket_slot] = w;
    }
  }

  const r32L = r32.slice(0, 8);       const r32R = r32.slice(8);

  // R16: place real fixtures, then fill null slots with synthetics from R32 winners.
  // R16 idx i is fed by R32 slots 2i+1 and 2i+2.
  const r16all = placeInBracket(byStage["r16"] || [], 8);
  for (let i = 0; i < 8; i++) {
    if (r16all[i] === null) {
      const w1 = r32WinnerBySlot[2 * i + 1];
      const w2 = r32WinnerBySlot[2 * i + 2];
      if (w1 || w2) r16all[i] = makeSynthetic("r16", w1, w2);
    }
  }

  // Build R16 winner map (from real fixtures only) for QF synthetics.
  const r16WinnerByIdx = {};
  for (let i = 0; i < 8; i++) {
    const fx = r16all[i];
    if (fx && !fx._synthetic) { const w = getWinner(fx); if (w) r16WinnerByIdx[i] = w; }
  }

  const r16L = r16all.slice(0, 4);    const r16R = r16all.slice(4);

  // QF: place real fixtures, then fill null slots from R16 winners.
  // QF idx j is fed by R16 idx 2j and 2j+1.
  const qfall = placeInBracket(byStage["qf"] || [], 4);
  for (let j = 0; j < 4; j++) {
    if (qfall[j] === null) {
      const w1 = r16WinnerByIdx[2 * j];
      const w2 = r16WinnerByIdx[2 * j + 1];
      if (w1 || w2) qfall[j] = makeSynthetic("qf", w1, w2);
    }
  }

  // Build QF winner map for SF synthetics.
  const qfWinnerByIdx = {};
  for (let j = 0; j < 4; j++) {
    const fx = qfall[j];
    if (fx && !fx._synthetic) { const w = getWinner(fx); if (w) qfWinnerByIdx[j] = w; }
  }

  const qfL  = qfall.slice(0, 2);     const qfR  = qfall.slice(2);

  // SF: place real fixtures, then fill null slots from QF winners.
  // SF idx k is fed by QF idx 2k and 2k+1.
  const sfall = placeInBracket(byStage["sf"] || [], 2);
  for (let k = 0; k < 2; k++) {
    if (sfall[k] === null) {
      const w1 = qfWinnerByIdx[2 * k];
      const w2 = qfWinnerByIdx[2 * k + 1];
      if (w1 || w2) sfall[k] = makeSynthetic("sf", w1, w2);
    }
  }

  const sfL  = sfall.slice(0, 1);     const sfR  = sfall.slice(1);
  const fin = byStage["final"] || [];
  const tp  = byStage["third_place"] || [];

  // Fixed-height match card
  function BracketCard({ fx }) {
    const played = fx?.is_final;
    const homeWon = played && (fx.penalty_winner_id
      ? fx.penalty_winner_id === fx.home_team_id
      : (fx.home_goals ?? 0) > (fx.away_goals ?? 0));
    const awayWon = played && !homeWon;

    function Side({ team, flag, goals, pk, won }) {
      const owner = team ? teamOwners?.[team] : null;
      return (
        <Box h={`${CARD_H / 2}px`} px={2}
          display="flex" alignItems="center" justifyContent="space-between"
          bg={won ? TOP_BAR_BLUE : "white"} color={won ? "white" : "black"}>
          <HStack spacing={1} flex={1} minW={0}>
            {flag && <FlagImg code={flag} size={15} />}
            <VStack align="start" spacing={0} minW={0}>
              <Text fontSize="xs" fontWeight={won ? "bold" : "normal"} noOfLines={1}>{team ?? "TBD"}</Text>
              {owner && <Text fontSize="2xs" color={won ? "whiteAlpha.700" : "gray.400"} noOfLines={1}>{owner}</Text>}
            </VStack>
          </HStack>
          {played && (
            <Text fontSize="xs" fontWeight="bold" color={won ? "yellow.200" : "gray.500"} flexShrink={0}>
              {goals ?? 0}{pk ? "P" : ""}
            </Text>
          )}
        </Box>
      );
    }

    return (
      <Box w={`${COL_W}px`} h={`${CARD_H}px`} overflow="hidden"
        border="1.5px solid" borderColor={played ? TOP_BAR_BLUE : "gray.300"}
        borderRadius="md" bg="white" boxShadow="sm">
        <Side team={fx?.home_team} flag={fx?.home_flag_code} goals={fx?.home_goals}
          pk={fx?.penalty_winner_id === fx?.home_team_id} won={homeWon} />
        <Box h="1px" bg={played ? `${TOP_BAR_BLUE}80` : "gray.200"} />
        <Side team={fx?.away_team} flag={fx?.away_flag_code} goals={fx?.away_goals}
          pk={fx?.penalty_winner_id === fx?.away_team_id} won={awayWon} />
      </Box>
    );
  }

  // Column of cards, absolutely positioned
  function RoundCol({ matches, numSlots }) {
    return (
      <Box position="relative" h={`${HALF_H}px`} w={`${COL_W}px`} flexShrink={0}>
        {Array.from({ length: numSlots }, (_, i) => (
          <Box key={i} position="absolute" top={`${cardTop(i, numSlots)}px`} left={0}>
            <BracketCard fx={matches[i] ?? null} />
          </Box>
        ))}
      </Box>
    );
  }

  // Merges numOutSlots slots (left) → numOutSlots/2 (right)
  function LeftConn({ numOutSlots }) {
    const sH = HALF_H / numOutSlots;
    const half = CONN_W / 2;
    return (
      <Box position="relative" h={`${HALF_H}px`} w={`${CONN_W}px`} flexShrink={0}>
        {Array.from({ length: numOutSlots / 2 }, (_, i) => {
          const y1 = (i * 2 + 0.5) * sH;
          const y2 = (i * 2 + 1.5) * sH;
          const yM = (y1 + y2) / 2;
          return (
            <React.Fragment key={i}>
              <Box position="absolute" top={`${y1}px`} left={0}          w={`${half}px`}   h="1px" bg="gray.400" />
              <Box position="absolute" top={`${y2}px`} left={0}          w={`${half}px`}   h="1px" bg="gray.400" />
              <Box position="absolute" top={`${y1}px`} left={`${half}px`} w="1px" h={`${y2 - y1}px`} bg="gray.400" />
              <Box position="absolute" top={`${yM}px`} left={`${half}px`} w={`${half}px`} h="1px"  bg="gray.400" />
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  // Splits numOutSlots/2 (left) → numOutSlots (right)
  function RightConn({ numOutSlots }) {
    const sH = HALF_H / numOutSlots;
    const half = CONN_W / 2;
    return (
      <Box position="relative" h={`${HALF_H}px`} w={`${CONN_W}px`} flexShrink={0}>
        {Array.from({ length: numOutSlots / 2 }, (_, i) => {
          const y1 = (i * 2 + 0.5) * sH;
          const y2 = (i * 2 + 1.5) * sH;
          const yM = (y1 + y2) / 2;
          return (
            <React.Fragment key={i}>
              <Box position="absolute" top={`${yM}px`} left={0}          w={`${half}px`}   h="1px" bg="gray.400" />
              <Box position="absolute" top={`${y1}px`} left={`${half}px`} w="1px" h={`${y2 - y1}px`} bg="gray.400" />
              <Box position="absolute" top={`${y1}px`} left={`${half}px`} w={`${half}px`} h="1px"  bg="gray.400" />
              <Box position="absolute" top={`${y2}px`} left={`${half}px`} w={`${half}px`} h="1px"  bg="gray.400" />
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  // Simple horizontal line (SF ↔ Final)
  function StraightConn() {
    return (
      <Box position="relative" h={`${HALF_H}px`} w={`${CONN_W}px`} flexShrink={0}>
        <Box position="absolute" top={`${HALF_H / 2}px`} left={0} w={`${CONN_W}px`} h="1px" bg="gray.400" />
      </Box>
    );
  }

  function ColHeader({ label, w }) {
    return (
      <Box w={`${w}px`} flexShrink={0} textAlign="center">
        {label && (
          <Text fontSize="2xs" fontWeight="bold" color={TOP_BAR_BLUE}
            textTransform="uppercase" letterSpacing="0.5px" noOfLines={1}>
            {label}
          </Text>
        )}
      </Box>
    );
  }

  const LEFT_LBLS  = ["Round of 32", "R16", "Quarters", "Semis"];
  const RIGHT_LBLS = ["Semis", "Quarters", "R16", "Round of 32"];

  return (
    <Box overflowX="auto" pb={2}>
      <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.400"
        textAlign="center" mb={2}>← Scroll to see full bracket →</Text>
      <Box minW="fit-content">
        {/* Header */}
        <HStack spacing={0} mb={3}>
          {LEFT_LBLS.map((lbl, i) => (
            <React.Fragment key={`lh${i}`}>
              <ColHeader label={lbl} w={COL_W} />
              <ColHeader label={null} w={CONN_W} />
            </React.Fragment>
          ))}
          <ColHeader label="Final" w={COL_W} />
          {RIGHT_LBLS.map((lbl, i) => (
            <React.Fragment key={`rh${i}`}>
              <ColHeader label={null} w={CONN_W} />
              <ColHeader label={lbl} w={COL_W} />
            </React.Fragment>
          ))}
        </HStack>

        {/* Bracket */}
        <HStack spacing={0} align="flex-start">
          <RoundCol matches={r32L} numSlots={8} />
          <LeftConn  numOutSlots={8} />
          <RoundCol matches={r16L} numSlots={4} />
          <LeftConn  numOutSlots={4} />
          <RoundCol matches={qfL}  numSlots={2} />
          <LeftConn  numOutSlots={2} />
          <RoundCol matches={sfL}  numSlots={1} />
          <StraightConn />

          {/* Final (center) */}
          <Box flexShrink={0} w={`${COL_W}px`} h={`${HALF_H}px`} position="relative">
            <Box position="absolute" top={`${HALF_H / 2 - CARD_H / 2}px`} left={0}>
              <BracketCard fx={fin[0] ?? null} />
            </Box>
            {tp[0] && (
              <Box position="absolute" top={`${HALF_H / 2 + CARD_H / 2 + 20}px`} left={0} w={`${COL_W}px`}>
                <Text fontSize="2xs" color="gray.500" textAlign="center" fontWeight="bold"
                  textTransform="uppercase" mb={1}>3rd Place</Text>
                <BracketCard fx={tp[0]} />
              </Box>
            )}
          </Box>

          <StraightConn />
          <RoundCol matches={sfR}  numSlots={1} />
          <RightConn numOutSlots={2} />
          <RoundCol matches={qfR}  numSlots={2} />
          <RightConn numOutSlots={4} />
          <RoundCol matches={r16R} numSlots={4} />
          <RightConn numOutSlots={8} />
          <RoundCol matches={r32R} numSlots={8} />
        </HStack>
      </Box>
    </Box>
  );
}

function FixturesView({ fixtures, leaderboard }) {
  const [fxView, setFxView] = React.useState("date");
  const [groupExpanded, setGroupExpanded] = React.useState(true);

  // Build team name → owner username map
  const teamOwners = {};
  for (const entry of leaderboard || []) {
    for (const t of entry.teams || []) {
      if (!t.is_swapped_out) teamOwners[t.team_name] = entry.username;
    }
  }

  const groupFx    = fixtures.filter((fx) => fx.stage === "group");
  const knockoutFx = fixtures.filter((fx) => fx.stage !== "group");
  const hasKnockout = knockoutFx.length > 0;
  const groupStageDone = groupFx.length > 0 && groupFx.every((fx) => fx.is_final);

  // Auto-collapse group stage once all matches are final
  React.useEffect(() => {
    if (groupStageDone) setGroupExpanded(false);
  }, [groupStageDone]);

  // By Group
  const byGroup = {};
  for (const fx of groupFx) {
    const g = fx.group ?? "?";
    if (!byGroup[g]) byGroup[g] = [];
    byGroup[g].push(fx);
  }
  const groups = Object.keys(byGroup).sort();

  // By Date (group stage only — knockout lives in the bracket)
  const groupSorted = [...groupFx].sort((a, b) => {
    if (!a.kickoff_utc && !b.kickoff_utc) return 0;
    if (!a.kickoff_utc) return 1;
    if (!b.kickoff_utc) return -1;
    return new Date(a.kickoff_utc) - new Date(b.kickoff_utc);
  });
  const byDate = {};
  for (const fx of groupSorted) {
    const key = fx.kickoff_utc ? formatDateEST(fx.kickoff_utc) : "TBD";
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(fx);
  }
  const dateKeys = Object.keys(byDate);

  if (fixtures.length === 0) {
    return (
      <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
        <Text color="gray.500">No fixtures available yet.</Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {/* ── Knockout Bracket — promoted above group stage once it starts ── */}
      {hasKnockout && (
        <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
          <Heading size="md" color={TOP_BAR_BLUE} mb={4}>Knockout Bracket</Heading>
          <BracketView fixtures={knockoutFx} teamOwners={teamOwners} />
        </Box>
      )}

      {/* ── Group Stage — collapsible, auto-minimized when complete ── */}
      {groupFx.length > 0 && (
        <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
          <HStack
            justify="space-between" align="center" cursor="pointer"
            onClick={() => setGroupExpanded((v) => !v)}
          >
            <HStack spacing={2}>
              <Heading size="md" color={TOP_BAR_BLUE}>Group Stage Fixtures</Heading>
              {groupStageDone && (
                <Badge colorScheme="green" fontSize="xs">Complete</Badge>
              )}
            </HStack>
            <IconButton
              icon={groupExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              size="xs" variant="ghost" color={TOP_BAR_BLUE}
              aria-label={groupExpanded ? "Collapse" : "Expand"}
              onClick={(e) => { e.stopPropagation(); setGroupExpanded((v) => !v); }}
            />
          </HStack>

          {groupExpanded && (
            <>
              <HStack mt={4} mb={4} spacing={2}>
                <Button size="sm" bg={fxView === "date" ? TOP_BAR_BLUE : "gray.100"} color={fxView === "date" ? "white" : "black"} _hover={{ bg: fxView === "date" ? TOP_BAR_BLUE : "gray.200" }} onClick={() => setFxView("date")}>By Date</Button>
                <Button size="sm" bg={fxView === "group" ? TOP_BAR_BLUE : "gray.100"} color={fxView === "group" ? "white" : "black"} _hover={{ bg: fxView === "group" ? TOP_BAR_BLUE : "gray.200" }} onClick={() => setFxView("group")}>By Group</Button>
              </HStack>

              {fxView === "date" && (
                <VStack align="stretch" spacing={4}>
                  {dateKeys.map((dateKey) => (
                    <Box key={dateKey}>
                      <Text fontSize="xs" fontWeight="bold" color={TOP_BAR_BLUE} textTransform="uppercase"
                        letterSpacing="1px" borderBottom="2px solid" borderColor={TOP_BAR_BLUE} pb={1} mb={2}>
                        {dateKey}
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {byDate[dateKey].map((fx) => (
                          <FixtureCard key={fx.id} fx={fx} teamOwners={teamOwners} showGroup={true} />
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              )}

              {fxView === "group" && (
                <Tabs size="sm" variant="soft-rounded" colorScheme="blue">
                  <TabList flexWrap="wrap" gap={1} mb={3}>
                    {groups.map((g) => <Tab key={g} fontSize="xs" px={3} py={1}>Group {g}</Tab>)}
                  </TabList>
                  <TabPanels>
                    {groups.map((g) => (
                      <TabPanel key={g} px={0} py={0}>
                        <VStack align="stretch" spacing={2}>
                          {byGroup[g]
                            .slice()
                            .sort((a, b) => new Date(a.kickoff_utc) - new Date(b.kickoff_utc))
                            .map((fx) => (
                              <FixtureCard key={fx.id} fx={fx} teamOwners={teamOwners} showDate={true} />
                            ))}
                        </VStack>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              )}
            </>
          )}
        </Box>
      )}
    </VStack>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function WorldCupActiveHome() {
  const { poolId }    = useParams();
  const navigate      = useNavigate();
  const { user, logout } = useAuth();
  const toast = useToast();

  const username    = user?.username || "Player";

  const [pool,         setPool]         = useState(null);
  const [draftState,   setDraftState]   = useState(null);
  const [leaderboard,  setLeaderboard]  = useState([]);
  const [fixtures,     setFixtures]     = useState([]);
  const [poolPayouts,  setPoolPayouts]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState("");
  const [picking,          setPicking]          = useState(null);
  const [beginning,        setBeginning]        = useState(false);
  const [togglingAutodraft, setTogglingAutodraft] = useState(false);
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [viewMode,       setViewMode]       = useState("group"); // "group" | "rank"
  const [showFullTeams,  setShowFullTeams]  = useState(false);

  const pollRef = useRef(null);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const poolData = await getWcPool(WC_SEASON_ID, poolId);
      setPool(poolData);

      if (poolData.status === "drafting") {
        const draft = await getWcDraftState(WC_SEASON_ID, poolId);
        setDraftState(draft);
      } else if (["active", "swap", "knockout", "complete"].includes(poolData.status)) {
        const [lb, fx, pays] = await Promise.all([
          getWcLeaderboard(WC_SEASON_ID, poolId),
          getWcFixtures(WC_SEASON_ID, poolId),
          getWcPayouts(WC_SEASON_ID, poolId),
        ]);
        setLeaderboard(lb);
        setFixtures(fx);
        setPoolPayouts(pays);
      }
      setLoadError("");
    } catch (err) {
      setLoadError(err?.response?.data?.detail || "Failed to load pool.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Poll every 15s during draft only; all other statuses load once on mount
  useEffect(() => {
    if (!pool || pool.status !== "drafting") return;
    pollRef.current = setInterval(() => loadAll(true), 15000);
    return () => clearInterval(pollRef.current);
  }, [pool, loadAll]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleBeginDraft() {
    setBeginning(true);
    try {
      await randomizeDraft(WC_SEASON_ID, poolId);
      await loadAll();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to start draft", status: "error", duration: 4000 });
    } finally {
      setBeginning(false);
    }
  }

  async function handlePick(teamId, onBehalfOfUserId = null) {
    setPicking(teamId);
    try {
      const newState = await makeWcPick(WC_SEASON_ID, poolId, teamId, onBehalfOfUserId);
      setDraftState(newState);
      // If draft completed, reload pool to update status
      if (newState.draft_complete) {
        const poolData = await getWcPool(WC_SEASON_ID, poolId);
        setPool(poolData);
      }
    } catch (err) {
      toast({
        title: "Pick failed",
        description: err?.response?.data?.detail || "Could not submit pick. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPicking(null);
    }
  }

  async function handleToggleAutodraft() {
    setTogglingAutodraft(true);
    try {
      const currentEnabled = draftState?.user_autodraft_enabled ?? false;
      const result = await setWcAutodraft(WC_SEASON_ID, poolId, !currentEnabled);
      // If it was the user's turn and autodraft fired immediately,
      // the server returns a full draft state (has draft_order).
      // Otherwise it returns just {autodraft_enabled: bool}.
      if (result.draft_order) {
        setDraftState(result);
        if (result.draft_complete) {
          const poolData = await getWcPool(WC_SEASON_ID, poolId);
          setPool(poolData);
        }
      } else {
        setDraftState((prev) => prev ? { ...prev, user_autodraft_enabled: result.autodraft_enabled } : prev);
      }
    } catch (err) {
      toast({ title: "Failed to update autodraft", status: "error", duration: 3000 });
    } finally {
      setTogglingAutodraft(false);
    }
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading World Cup Pool...</Text>
        </Flex>
      </Box>
    );
  }

  const poolName  = pool?.name ?? "";
  const status    = pool?.status ?? "pending";
  const isArchived = !!pool?.is_archived;
  // A World Cup pool is done when the tournament finishes or it is archived.
  const poolFinished = isArchived || status === "complete";

  // ── Archived view ──────────────────────────────────────────────────────────
  if (isArchived) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <PageHeader
          title="WORLD CUP POOL"
          onBack={() => navigate("/world-cup")}
          user={user}
          onLogout={handleLogout}
        />
        <Container maxW="container.xl" py={8}>
          <WcFinalResultsBox leaderboard={leaderboard} payouts={poolPayouts} poolName={poolName} />
          <LeaderboardView leaderboard={leaderboard} currentUsername={username}
                           poolName={poolName} poolFinished={poolFinished} />
        </Container>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="WORLD CUP POOL"
        onBack={() => navigate("/world-cup")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Draft Order Sidebar (drafting only) ── */}
      {status === "drafting" && draftState && (
        <Box
          position="fixed"
          left={0}
          top="50%"
          transform="translateY(-50%)"
          zIndex={200}
          display={{ base: "none", md: "flex" }}
          alignItems="center"
        >
          {/* Sliding panel */}
          <Box
            w={sidebarOpen ? "260px" : "0px"}
            overflowX="hidden"
            transition="width 0.25s ease"
            bg="white"
            borderRight={`3px solid ${TOP_BAR_BLUE}`}
            boxShadow="4px 0 16px rgba(0,0,0,0.15)"
          >
            <Box w="260px" maxH="85vh" overflowY="auto" p={4} color="black">
              <Heading size="sm" color={TOP_BAR_BLUE} mb={1}>Draft Order</Heading>
              <Text fontSize="xs" color="gray.500" mb={3}>
                {draftState.draft_complete
                  ? "Draft complete!"
                  : `Round ${draftState.current_round} · Pick ${draftState.total_picks + 1} / ${draftState.max_picks}`}
              </Text>
              <VStack align="stretch" spacing={2}>
                {(draftState.draft_order || []).map((d, idx) => {
                  const posColors = ["blue.400","green.400","orange.400","red.400","purple.400","teal.400","pink.400","yellow.500","cyan.400","gray.500","blue.600","green.600"];
                  const color = posColors[idx] ?? "gray.400";
                  return (
                    <Box
                      key={d.user_id}
                      p={2}
                      borderRadius="md"
                      bg={d.is_current_picker ? "yellow.50" : "gray.50"}
                      border={d.is_current_picker ? "2px solid" : "1px solid"}
                      borderColor={d.is_current_picker ? "yellow.400" : "gray.200"}
                    >
                      <HStack spacing={2} mb={1}>
                        <Circle size="22px" bg={color} color="white" fontSize="xs" fontWeight="bold">
                          {d.position}
                        </Circle>
                        <Text fontSize="sm" fontWeight={d.is_current_picker ? "bold" : "normal"} color={TOP_BAR_BLUE} noOfLines={1} flex={1}>
                          {d.username}{d.is_current_picker && !draftState.draft_complete && " ⬅"}
                        </Text>
                        {d.autodraft_enabled && (
                          <Box
                            fontSize="10px"
                            fontWeight="bold"
                            color="white"
                            bg="orange.400"
                            borderRadius="sm"
                            px="4px"
                            py="1px"
                            flexShrink={0}
                          >
                            A
                          </Box>
                        )}
                      </HStack>
                      {d.picks && d.picks.length > 0 ? (
                        <VStack align="start" spacing={0} pl={7}>
                          {d.picks.map((p) => (
                            <Text key={p.team_id} fontSize="xs" color="gray.600" noOfLines={1}>{p.team_name}</Text>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="xs" color="gray.500" pl={7}>No picks yet</Text>
                      )}
                    </Box>
                  );
                })}
              </VStack>
              <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={3}>A = Autodraft</Text>
            </Box>
          </Box>

          {/* Vertical tab */}
          <Box
            onClick={() => setSidebarOpen((o) => !o)}
            cursor="pointer"
            color="white"
            border="1px solid rgba(255,255,255,0.4)"
            bg="orange.400"
            _hover={{ bg: "orange.500" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px="4px"
            py="10px"
            borderTopRightRadius="sm"
            borderBottomRightRadius="sm"
            flexShrink={0}
            userSelect="none"
            fontSize="14px"
            fontWeight="semibold"
            letterSpacing="0.5px"
            boxShadow="2px 0 6px rgba(0,0,0,0.3)"
          >
            <Text
              style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
              whiteSpace="nowrap"
            >
              Draft Order
            </Text>
          </Box>
        </Box>
      )}

      {/* ── Page body ── */}
      <Container maxW="container.xl" py={8}>

        {loadError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />
            {loadError.toLowerCase().includes("bearer") || loadError.toLowerCase().includes("token")
              ? "Your session has expired. Please log in again."
              : loadError}
            {(loadError.toLowerCase().includes("bearer") || loadError.toLowerCase().includes("token")) && (
              <Button ml={4} size="sm" colorScheme="red" onClick={() => navigate("/login")}>
                Log In
              </Button>
            )}
          </Alert>
        )}

        {["active", "swap", "knockout", "complete"].includes(status) && !isArchived && (
          <SmackBoard
            poolId={Number(poolId)}
            seasonId={WC_SEASON_ID}
            apiFns={{ getMessages: getWcSmackMessages, postMessage: postWcSmackMessage, getReplies: getWcSmackReplies, postReply: postWcSmackReply, react: reactWcSmack, deleteMessage: deleteWcSmackMessage }}
          />
        )}

        {/* Status + action bar */}
        <HStack mb={6} spacing={{ base: 2, md: 4 }} flexWrap="nowrap" align="center">
          {/* Combined draft status box — only shown while actively drafting */}
          {status === "drafting" && draftState && !draftState.draft_complete && (
            <Box
              px={4}
              py={2}
              borderRadius="md"
              bg={draftState.current_picker_user_id === user?.id ? "yellow.100" : "orange.400"}
              border="2px solid"
              borderColor={draftState.current_picker_user_id === user?.id ? "yellow.400" : "orange.400"}
            >
              <Text
                fontWeight="bold"
                fontSize="sm"
                color={draftState.current_picker_user_id === user?.id ? "yellow.800" : "white"}
              >
                {draftState.current_picker_user_id === user?.id
                  ? "Draft in Progress: It's your turn — select a country below"
                  : `Draft in Progress: Waiting for ${(draftState.draft_order || []).find((d) => d.is_current_picker)?.username ?? "..."} to pick...`}
              </Text>
            </Box>
          )}

          {["active", "swap", "knockout", "complete"].includes(status) && (
            <>
              <Button
                size={{ base: "xs", md: "sm" }}
                bg={showFullTeams ? "white" : "whiteAlpha.200"}
                color={showFullTeams ? TOP_BAR_BLUE : "white"}
                border="1px solid"
                borderColor="whiteAlpha.400"
                _hover={{ bg: showFullTeams ? "gray.100" : "whiteAlpha.300" }}
                onClick={() => setShowFullTeams((v) => !v)}
              >
                {showFullTeams ? "Leaderboard View" : "Full Teams View"}
              </Button>
              {pool?.swap_enabled && (
                <Button
                  size={{ base: "xs", md: "sm" }}
                  bg="red.600"
                  color="white"
                  _hover={{ bg: "red.700" }}
                  onClick={() => navigate(`/world-cup/current/${poolId}/swap`)}
                >
                  Manage Swap
                </Button>
              )}
            </>
          )}

          <Box flex="1" display={{ base: "none", md: "block" }} />

          {(pool?.creator_user_id === user?.id || pool?.current_user_is_co_admin) && (
            <Button
              size={{ base: "xs", md: "sm" }}
              variant="outline"
              color="white"
              borderColor="whiteAlpha.500"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => navigate(`/world-cup/setup/${poolId}`)}
            >
              Pool Settings
            </Button>
          )}
        </HStack>

        {/* ── Status-based content ── */}
        {status === "pending" && pool && (
          <PendingView
            pool={pool}
            isCreator={pool.creator_user_id === user?.id || !!user?.is_admin}
            onBeginDraft={handleBeginDraft}
            beginning={beginning}
          />
        )}

        {status === "drafting" && draftState && (
          <DraftView
            draftState={draftState}
            currentUserId={user?.id}
            isCreator={pool?.creator_user_id === user?.id || !!user?.is_admin}
            onPick={handlePick}
            picking={picking}
            viewMode={viewMode}
            setViewMode={setViewMode}
            autodraftEnabled={draftState.user_autodraft_enabled ?? false}
            onToggleAutodraft={handleToggleAutodraft}
            togglingAutodraft={togglingAutodraft}
          />
        )}

        {["active", "swap", "knockout", "complete"].includes(status) && (
          <VStack align="stretch" spacing={6}>
            {showFullTeams ? (
              <FullTeamsView leaderboard={leaderboard} poolFinished={poolFinished} />
            ) : (
              <>
                <LeaderboardView leaderboard={leaderboard} currentUsername={username}
                                 poolName={poolName} poolFinished={poolFinished} />
                <FixturesView fixtures={fixtures} leaderboard={leaderboard} />
              </>
            )}
          </VStack>
        )}

      </Container>

      <RulesPayoutsPanel
        rules={WC_RULES}
        payouts={poolPayouts}
        totalPot={pool?.total_pot}
      />
    </Box>
  );
}
